import React, { useState, useEffect, useContext, useMemo } from "react";
import styles from "./ForWhomMainPage.module.scss";
import { useFormikContext } from "formik";
import {
  OwcTypography,
  OwcListItem,
  OwcIconButton,
  OwcInput,
  OwcIcon,
  OwcRadioGroup,
  OwcRadio,
  OwcCheckbox,
} from "@one/react";
import {
  WHOM_TAB_LABEL,
  MAX_COUNT_CAPACITY,
  ATTRIBUTES_HEADING,
  CLUSTER_DETAILS_ATTRIBUTE_META,
  STANDALONE_ATTRIBUTE_META,
  entryType,
  DURATION,
  INFORMATION_BOOKING_TYPE,
  INFORMATION_BOOKING_DESCRIPTION_MAX,
} from "../../../constants";
import { withApollo, compose } from "react-apollo";
import { connect, useSelector, useDispatch } from "react-redux";
import { selectIsPredOrGredUser } from "../../../selectors/user";
import { loadUserProjects as loadUserProjectsAction } from "../../user/redux/actions";
import { GET_AVAILABLE_PROJECTS } from "../../../gql/bookingapi/queries";
import {
  RichDescriptionEditor,
  deriveShortDescription,
  MAX_RICH_DESCRIPTION_LENGTH,
} from "../../../components/shared/RichDescription";
import { sortBy } from "lodash";
import PopOverModal from "../../../components/shared/CommonPopOverModal/PopOverModal";
import { GridAutoCompleteStyles } from "../../../components/shared/GridAutoCompleteStyles";
import { formatUserDisplay, uniqList } from "../../../utils/helpers/text/index";
import { convertNumericValueToBoolean } from "../../../utils/helpers/data";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import { useEligibleBookingForUsers } from "./useEligibleBookingForUsers";
import { loadBookingInstruments as loadBookingInstrumentsAction } from "../../booking-instruments/redux/actions";
import { loadPendingReservations as loadPendingReservationsAction } from "../../booking-calendars/redux/actions";
import { CustomFilterContext } from "../../booking-instruments/booking-instruments-custom-filter/context";
import useDialog from "../../../utils/hooks/useDialog";
import PopoverAttributesDetails from "../../../components/shared/CommonPopOverModal/PopoverAttributesDetails";
import PopOverClusterAgGridTable from "../../../components/shared/CommonPopOverModal/PopOverClusterAgGridTable";
import { BookingContext, BookingPopUpContext } from "../../booking/context";
import RecurringBookingContext from "../calendar-timeline/RecurringBooking/Context/RecurringBookingContext";
import { snackbarService } from "@one/web-components/dist/index.cjs.js";
import { SNACKBAR_DURATION } from "../../../components/EquipmentList/Constants/constants";
import { setForceInformationOnly } from "../../booking-calendars/redux/actions";
import { detectOverlapWithinPending } from "../calendar-timeline/overlapDetection.utils";
import { ActionTypeSelect, ElnExperimentField } from "@digitallab/grid-common-frontend-components";
import AssistenceDeclaration from "../../../components/AssistenceDeclaration";
import HazardousDeclaration from "../../../components/HazardousDeclaration";
import AssistantSelect from "../../../components/shared/AssistantSelect/AssistantSelect";

const ForWhomMainPage = ({
  loadBookingInstruments,
  loadPendingReservations,
  loadUserProjects,
  client
}) => {
  const actionTypes = Object.values(useSelector((store) => store.actionTypes));
  const formik = useFormikContext();
  const { isEditMode, editBookingData, isDuplicate } = useContext(BookingContext);
  const recurringCtx = useContext(RecurringBookingContext);
  const isRecurringBooking = Boolean(recurringCtx?.isRecurringBooking);
  const [bookingKind, setBookingKind] = useState(
    formik.values.bookingType === "Run"
      ? "Run"
      : formik.values.bookingType === INFORMATION_BOOKING_TYPE
        ? INFORMATION_BOOKING_TYPE
        : "Action",
  );

  const { dispatchAction: bookingPopUpDispatchAction } =
    useContext(BookingPopUpContext);
  const { setSelectedList } = useContext(CustomFilterContext);
  const usersList = useSelector((state) => state.users.users);
  const storeUser = useSelector((store) => store.user);
  const isPredUser = storeUser.isPredUser;
  const isPredOrGredUser = selectIsPredOrGredUser(storeUser);
  // RETINA-7943173 AC6: assistant-at-creation is only available on pRED/gRED sites.
  const canAssignAssistantAtCreation = isPredOrGredUser;
  const isBookingAdmin = storeUser?.roles?.some((role) => role === "BookingAdmin");
  const isInformationMode = bookingKind === INFORMATION_BOOKING_TYPE;
  const { openDialog, ...dialogProps } = useDialog();
  const bookingInstruments = useSelector((store) => store.bookingInstruments);
  const { pendingReservations } = useSelector((state) => state.reservations);
  const forceInformationOnly = useSelector((state) => state.reservations.forceInformationOnly);
  const dispatch = useDispatch();
  // Belt-and-suspenders: also derive overlap-within-pending here, because
  // CalendarMainPage (where the Redux flag is normally set) unmounts when the
  // wizard advances to step 3 -- if the last drag-create in step 2 happened in
  // the same commit as the tab change, the flag may not have been dispatched.
  // Recomputing here guarantees the lock fires whenever pending slots actually
  // overlap each other, regardless of what step 2 managed to dispatch.
  const pendingsOverlapEachOther = useMemo(
    () => detectOverlapWithinPending(pendingReservations),
    [pendingReservations],
  );
  const isOverlapForcedInformation =
    (!!forceInformationOnly || pendingsOverlapEachOther) && isBookingAdmin && !isEditMode && !isDuplicate && !isRecurringBooking;
  const [instrumentDetails, setInstrumentDetails] = useState(
    bookingInstruments.bookingInstruments,
  );
  const [manualProjectInput, setManualProjectInput] = useState(false);
  const [isProjectSetFromRedpandaProjects, setIsProjectSetFromRedpandaProjects] = useState(false);
  const [projectOptions, setProjectOptions] = useState([]);
  const [popoverObject, setpopoverObject] = useState({
    equipmentData: {},
    eventData: {},
  });

  const [showHazardousDeclaration, setShowHazardousDeclaration] = useState(false);
  const [showAssistedDecleration, setShowAssistedDecleration] = useState(false);

  const [isRefreshingProjects, setIsRefreshingProjects] = useState(false);

  const getProjectOptionLabel = (project = {}) => {
    const themeNumber = project.themeNumber;
    const shortName = project.projectShortName || project.projectName || project.projectId;
    if (themeNumber && shortName) return `${themeNumber} - ${shortName}`;
    return shortName || themeNumber || "-";
  };

  const compareProjects = (projectA = {}, projectB = {}) => {
    const themeA = String(projectA.themeNumber || "");
    const themeB = String(projectB.themeNumber || "");
    const themeCompare = themeA.localeCompare(themeB, undefined, {
      numeric: true,
      sensitivity: "base",
    });
    if (themeCompare !== 0) return themeCompare;

    const shortNameA = String(
      projectA.projectShortName || projectA.projectName || projectA.projectId || ""
    );
    const shortNameB = String(
      projectB.projectShortName || projectB.projectName || projectB.projectId || ""
    );

    return shortNameA.localeCompare(shortNameB, undefined, {
      numeric: true,
      sensitivity: "base",
    });
  };

  const equipmentData = useMemo(() => popoverObject?.equipmentData,
    [popoverObject?.equipmentData])

  const showRedpandaErrorToast = () => snackbarService.show({
    message: `Unable to retrieve available projects, ${!!localStorage.getItem("secondaryAuthAccessToken") ? 'try refresh on the project field or ' : ''}enter project manually.`,
    type: "error",
    duration: DURATION,
  });

  useEffect(() => {
    const { withAssistance, hasHazardousMaterials } = formik.values;
    const instruments = bookingInstruments?.bookingInstruments || [];

    // Check if any equipment requires assistance
    const requiresAssistance = instruments.some(
      (eq) => convertNumericValueToBoolean(eq?.shouldAssisted) || convertNumericValueToBoolean(eq?.assistedBooking)
    );

    // Check if any equipment is hazardous
    const requiresHazardous = instruments.some((eq) => convertNumericValueToBoolean(eq?.isHazardous));

    // Only show declarations if equipment requires them AND formik values are defined
    if (withAssistance !== undefined && requiresAssistance) {
      setShowAssistedDecleration(true);
    } else setShowAssistedDecleration(false);

    if (hasHazardousMaterials !== undefined && requiresHazardous) {
      setShowHazardousDeclaration(true);
    } else setShowHazardousDeclaration(false);

  }, [formik.values, bookingInstruments.bookingInstruments]);

  const refreshRedPandaProjects = async () => {
    if (!isPredUser || isRefreshingProjects) return;

    setIsRefreshingProjects(true);
    try {
      const jwtToken = localStorage.getItem("secondaryAuthAccessToken");
      const userId = storeUser?.id || storeUser?.userId || storeUser?.user;
      const email = storeUser?.email;
      if (!jwtToken) return
      const result = await client.query({
        query: GET_AVAILABLE_PROJECTS,
        fetchPolicy: "network-only",
        variables: {
          userId,
          email,
          token: jwtToken,
          timeoutMs: 24000,
        },
      });
      const projects = result?.data?.getAvailableProjectsFromRedPanda;
      if (Array.isArray(projects)) {
        loadUserProjects(projects);
      } else {
        showRedpandaErrorToast()
      }
    } catch (e) {
      showRedpandaErrorToast()
    } finally {
      setIsRefreshingProjects(false);
    }
  };

  useEffect(() => {
    if (Array.isArray(storeUser?.redPandaProjects)) {
      const sortedProjects = [...storeUser.redPandaProjects].sort(compareProjects);
      const mapped = sortedProjects.map(project => {
        const name = getProjectOptionLabel(project);
        return {
          value: {
            themeNumber: project.themeNumber,
            projectId: project.projectId,
            projectShortName: project.projectShortName,
            projectName: project.projectName,
          },
          name
        }
      });
      setProjectOptions(mapped);
    } else {
      setProjectOptions([]);
    }
  }, [storeUser?.redPandaProjects]);

  useEffect(() => {
    if (isEditMode || isDuplicate) {
      const currentProjectId = formik.values.project;
      if (currentProjectId && projectOptions.length > 0 && !isProjectSetFromRedpandaProjects) {
        const matched = projectOptions.find(p => p.value?.projectId === currentProjectId);
        if (matched) {
          setManualProjectInput(false);
          formik.setFieldValue("project", "");
          formik.setFieldValue("redPandaProjects", [matched.value]);
          setIsProjectSetFromRedpandaProjects(true);
        }
      }

      const fromBooking = Array.isArray(formik.values.redPandaProjects)
        ? formik.values.redPandaProjects
        : [];

      if (fromBooking.length && !isProjectSetFromRedpandaProjects) {
        setManualProjectInput(false);
        setProjectOptions((prev) => {
          const existingIds = new Set(
            prev.map((o) => o.value?.projectId).filter(Boolean)
          );
          const additions = fromBooking
            .filter((p) => p?.projectId && !existingIds.has(p.projectId))
            .map((p) => ({
              value: p,
              name: getProjectOptionLabel(p),
            }));
          if (!additions.length) return prev;
          return [...prev, ...additions].sort((a, b) => compareProjects(a?.value, b?.value));
        });


        formik.setFieldValue("redPandaProjects", fromBooking);
        setIsProjectSetFromRedpandaProjects(true);
      }
    } // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditMode, projectOptions, formik.values.project, formik.values.redPandaProjects, manualProjectInput]);

  useEffect(() => {
    if ((isEditMode && !formik.values?.redPandaProjects?.length) || !isPredUser) {
      setManualProjectInput(true)
    } // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditMode, isPredUser, formik.values.redPandaProjects]);

  useEffect(() => {
    if (!isOverlapForcedInformation) return undefined;
    if (bookingKind !== INFORMATION_BOOKING_TYPE) {
      setBookingKind(INFORMATION_BOOKING_TYPE);
      // Bulk setValues + setErrors guarantees a single revalidation pass
      // that clears any stale hazardous/assistance error entries. Sequential
      // setFieldValue calls can leave old formik.errors around when validation
      // schedules interleave and the button stays disabled with hidden errors.
      formik.setValues(
        {
          ...formik.values,
          bookingType: INFORMATION_BOOKING_TYPE,
          elnId: "",
          hasHazardousMaterials: undefined,
          hazardousMaterialsText: "",
          withAssistance: undefined,
          assistanceReason: "",
          assistant: "",
          assistentTime: null,
        },
        true,
      );
      formik.setErrors({});
    }
    snackbarService.show({
      message:
        "Selected slots overlap (with an existing booking or with each other) - only Information type is available.",
      type: "warning",
      duration: SNACKBAR_DURATION,
    });
    return () => {
      dispatch(setForceInformationOnly(false));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOverlapForcedInformation]);

  useEffect(() => {
    let pendingReservationIDs = [...pendingReservations];
    pendingReservationIDs = pendingReservationIDs.map((k) => k.resource);
    let result =
      (instrumentDetails.length !== 0 &&
        instrumentDetails.filter((a) =>
          pendingReservationIDs.includes(a.inventoryId),
        )) ||
      [];
    setInstrumentDetails(result);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingReservations]);

  const currentUserOption = useMemo(() => ({
      key: storeUser.email,
      familyName: storeUser.familyName,
      givenName: storeUser.givenName,
      name: storeUser.name,
      userId: storeUser.user,
      value: formatUserDisplay(storeUser),
      email: storeUser.email,
  }), [storeUser]);
  const bookingCreatedBy = editBookingData?.createdBy ?? "";
  // A group-view prefill draft resolves its own explicit "Booking for" list;
  // if the current user isn't part of it, they must not be force-added below.
  const isGroupViewPrefillBooking = editBookingData?.prefillSource === "group-view-url";
  const userOptions = useMemo(
    () => usersList.map((item) => ({
      ...item,
      key: item.email,
      value: formatUserDisplay(item),
    })),
    [usersList],
  );
  const userDetail = useMemo(() => {
    if (!isEditMode) return [currentUserOption];
    const creator = userOptions.filter((candidate) => candidate.userId === bookingCreatedBy);
    if (creator.length > 0 || isGroupViewPrefillBooking) return creator;
    return [currentUserOption];
  }, [isEditMode, userOptions, bookingCreatedBy, isGroupViewPrefillBooking, currentUserOption]);
  const fixedOptions = useMemo(() => (userDetail[0] ? [userDetail[0]] : []), [userDetail]);
  const [value, setValue] = useState([
    ...fixedOptions,
    ...formik?.values?.bookingFor?.filter(
      (k) => k && k.userId !== userDetail[0]?.userId
    ),
  ]);

  const candidateOptions = useMemo(() => {
    const selectableUsers = userOptions.filter((candidate) =>
      isEditMode ? candidate.userId !== bookingCreatedBy : candidate.key !== storeUser.email,
    );
    return uniqList([...userDetail, ...sortBy(selectableUsers, "value")], "value");
  }, [userOptions, isEditMode, bookingCreatedBy, storeUser.email, userDetail]);

  // Keep the candidate list consistent with backend validation: non-admins
  // must have access themselves and for every user the booking is for.
  const currentUserId = storeUser.user;
  const isBookingForSelf = value.some((option) => option?.userId === currentUserId);
  const { isUserEligible, isFiltering, eligibleUserIds } = useEligibleBookingForUsers(
    bookingInstruments?.bookingInstruments,
    client,
    candidateOptions,
  );
  const shouldRestrictToEligibleUsers = isFiltering && !isBookingAdmin;
  const isCreatorEligible = isUserEligible(currentUserOption);
  const creatorAccessError = shouldRestrictToEligibleUsers && !isCreatorEligible;
  const listOption = useMemo(() => {
    if (creatorAccessError) return [];
    return shouldRestrictToEligibleUsers
      ? candidateOptions.filter(isUserEligible)
      : candidateOptions;
  },
    [candidateOptions, creatorAccessError, shouldRestrictToEligibleUsers, isUserEligible],
  );

  useEffect(() => {
    if (!shouldRestrictToEligibleUsers) return;
    setValue((current) => {
      const filtered = creatorAccessError
        ? []
        : (current || []).filter((option) => isUserEligible(option));
      return filtered.length === current.length ? current : filtered;
    });
  }, [eligibleUserIds, creatorAccessError, shouldRestrictToEligibleUsers, isUserEligible]);

  useEffect(() => {
    if (isEditMode) {
      if (editBookingData) {
        editBookingData.bookedUsers = (editBookingData.bookedUsers || []).filter(Boolean);
      }
      let bookedUser = editBookingData?.bookedUsers.map((item) => ({
        ...item,
        key: item.email,
        userId: item.user || item.userId,
        value: formatUserDisplay(item),
      }));
      let existingUsersID = (bookedUser || []).map((k) => k.userId);
      let updatedUsers = (formik?.values?.bookingFor || []).filter(
        (k) => !existingUsersID?.includes(k.userId),
      );
      let updatedBookedUsers = (formik?.values?.bookingFor || []).map((i) =>
        bookedUser?.find((k) => i.userId === k.userId),
      );
      updatedBookedUsers = updatedBookedUsers?.filter((v) => v !== undefined);
      updatedBookedUsers = updatedBookedUsers?.filter(
        (k) => k.userId !== userDetail[0]?.userId,
      );
      if (updatedUsers.length === 0) {
        setValue([
          ...fixedOptions,
          ...updatedBookedUsers.filter((k) => k.userId !== bookingCreatedBy),
        ]);
      } else if (updatedUsers.length > 0) {
        const finalUsersList = [...fixedOptions, ...updatedBookedUsers, ...updatedUsers].map(user => {
          return {
            ...user,
            userId: user.userId || bookedUser.find(e => e.email === user.email)?.userId
          }
        }).filter((u, i, arr) => arr.findIndex(x => x.userId === u.userId) === i);
        setValue(finalUsersList);
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditMode]);

  useEffect(() => {
    formik.setValues({
      ...formik.values,
      bookingFor: value,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const handleOnClose = () => {
    bookingPopUpDispatchAction({
      type: "reset",
    });
  };

  const onClear = (ev) => {
    let result = instrumentDetails.filter((a) => a !== ev?.target?.value);
    let remainingPendingReservation = [...pendingReservations].filter(
      (a) => a.resource !== ev?.target?.value.inventoryId,
    );
    if (!result.length) {
      setInstrumentDetails([]);
      updateEquipmentDetailsInRedux([]);
      loadPendingReservations([]);
      setSelectedList([]);
    } else {
      setInstrumentDetails(result);
      updateEquipmentDetailsInRedux(result);
      loadPendingReservations([...remainingPendingReservation]);
      setSelectedList(() => {
        return result.map((k) => k.inventoryId);
      });
    }
  };

  useEffect(() => {
    if (instrumentDetails.length === 0) {
      formik.setValues({
        ...formik.values,
        description: "",
        richDescription: "",
        project: "",
        redPandaProjects: [],
      });
    } // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [instrumentDetails]);

  const renderTags = (selectedObjects) => {
    let renderTagValue = selectedObjects?.map((selectedObject) => {
      const chipLabel = selectedObject?.value ?? selectedObject;
      return selectedObjects.length === 1 ? `${chipLabel}` : `${chipLabel}, `;
    });
    let maxCapacity = MAX_COUNT_CAPACITY - renderTagValue[0].length;
    let remainingCapacity = maxCapacity - 7;
    if (remainingCapacity < 0) {
      remainingCapacity = 0;
    }
    return (
      <>
        <OwcTypography className={styles.renderTextField}>
          {renderTagValue[0]}
        </OwcTypography>
        {renderTagValue.length > 1 && (
          <OwcTypography style={{ marginLeft: "5px" }}>
            {renderTagValue[1].length > remainingCapacity
              ? `${renderTagValue[1].substring(0, remainingCapacity)}...`
              : renderTagValue[1]}
          </OwcTypography>
        )}
        {renderTagValue.length > 2 ? (
          <span style={{ color: "#0066CC", marginLeft: "5px" }}>
            +{renderTagValue.length - 2}
          </span>
        ) : (
          ""
        )}
      </>
    );
  };

  const updateEquipmentDetailsInRedux = async (result) => {
    let bookingInstruments = result;
    loadBookingInstruments({
      bookingInstruments: bookingInstruments,
    });
  };

  const inputChangeHandler = (event) => {
    const next = event.detail;
    const previous = bookingKind;
    setBookingKind(next);
    if (next === "Run") {
      formik.setFieldValue('bookingType', "Run");
    } else if (next === INFORMATION_BOOKING_TYPE) {
      formik.setFieldValue('bookingType', INFORMATION_BOOKING_TYPE);
      formik.setFieldValue('elnId', "");
      formik.setFieldValue('hasHazardousMaterials', undefined);
      formik.setFieldValue('hazardousMaterialsText', "");
      formik.setFieldValue('withAssistance', undefined);
      formik.setFieldValue('assistanceReason', "");
      formik.setFieldValue('assistant', "");
      formik.setFieldValue('assistentTime', null);
    } else {
      formik.setFieldValue('bookingType', null);
    }
    // When switching away from Information back to a regular booking, restore
    // the creator as a fixed participant in the bookingFor field.
    if (previous === INFORMATION_BOOKING_TYPE && next !== INFORMATION_BOOKING_TYPE) {
      setValue([...fixedOptions]);
      formik.setFieldValue('bookingFor', [...fixedOptions]);
    }
  };

  return (
    <>
      <div className={styles.flexContainer}>
        <div className={styles.flexLeftColumn}>
          <div className={styles.lineTitle}>
            <OwcTypography
              variant="subtitle2"
              className={styles.typographyTitle}
            >
              Equipment details
            </OwcTypography>
          </div>
          <div className={styles.scrollBarlist}>
            {Array.isArray(instrumentDetails) && instrumentDetails.length !== 0 &&
              instrumentDetails.map((item, index) => {
                const option = item.shortIdentifier;
                return (
                  <React.Fragment key={index}>
                    <OwcListItem
                      value={option}
                      key={option}
                      onClick={(e) => {
                        openDialog();
                        setpopoverObject({
                          equipmentData: item,
                          eventData: {},
                        });
                      }}
                      noBorder
                      className="owcListItemBorder"
                    >
                      <div style={{ display: "flex" }}>
                        <div style={{ width: "93%", paddingTop: "7px" }}>
                          {option}
                        </div>
                        <div style={{ width: "8%" }}>
                          <OwcIconButton
                            value={item}
                            key={option}
                            data-testid="clear-selected-button"
                            type="filled"
                            icon="circle_clear"
                            flat
                            onClick={(ev) => {
                              ev.stopPropagation();
                              const isAnyOnGoingEvent =
                                pendingReservations.filter(
                                  (x) =>
                                    x.resource ===
                                    ev.target.value.inventoryId &&
                                    x.isOngoingEventFlag,
                                );
                              if (isEditMode && isAnyOnGoingEvent.length > 0) {
                                bookingPopUpDispatchAction({
                                  type: "onGoingBooking",
                                  payload: {
                                    onApprove: handleOnClose,
                                  },
                                });
                              } else {
                                bookingPopUpDispatchAction({
                                  type: "futureBooking",
                                  payload: {
                                    onApprove: () => onClear(ev),
                                    onclose: handleOnClose,
                                  },
                                });
                              }
                            }}
                          />
                        </div>
                      </div>
                    </OwcListItem>
                  </React.Fragment>
                );
              })}
          </div>
        </div>
        <div className={styles.flexRightColumn}>
          <div className={styles.lineTitle}>
            <OwcTypography
              variant="subtitle2"
              className={styles.typographyTitle}
            >
              Booking details
            </OwcTypography>
          </div>
          <div>
            <div
              style={{
                padding: "10px 20px 10px 20px",
              }}
            >
              <div style={{ display: "flex" }}>
                <OwcRadioGroup
                  value={bookingKind}
                  onInputChange={inputChangeHandler}
                  style={{ width: "100%" }}
                >
                  <OwcRadio value="Run" disabled={isOverlapForcedInformation}>Run</OwcRadio>
                  {/* The Action Type dropdown lives on the same row as the Action
                      radio so it stays inline with it regardless of how many radios
                      follow (e.g. the admin-only Information option). */}
                  <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                    <OwcRadio value="Action" disabled={isOverlapForcedInformation}>Action</OwcRadio>
                    {bookingKind === "Action" && (
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <ActionTypeSelect
                          actionTypes={actionTypes}
                          formik={formik}
                          name="bookingType"
                        />
                      </div>
                    )}
                  </div>
                  {isBookingAdmin && !isRecurringBooking && (
                    <OwcRadio
                      value={INFORMATION_BOOKING_TYPE}
                      data-testid="whom-tab-bookingkind-information"
                    >
                      Information
                    </OwcRadio>
                  )}
                </OwcRadioGroup>
              </div>
            </div>
            <Autocomplete
              multiple
              data-testid="whom-tab-field-booking-for"
              value={value}
              limitTags={2}
              className={styles.bookForFilled}
              disableCloseOnSelect
              onChange={(_event, newValue) => {
                const nextValue = (Array.isArray(newValue) ? newValue : [newValue]).filter(Boolean);
                const currentUserSelected = nextValue.some((option) => option?.userId === currentUserId);
                setValue(!isPredOrGredUser && isBookingForSelf && !currentUserSelected
                  ? [currentUserOption, ...nextValue]
                  : nextValue);
              }}
              name="bookingFor"
              options={listOption}
              fullWidth={false}
              getOptionLabel={(option) => {
                if (Array.isArray(option) && option.length > 0) {
                  return option[0]?.value;
                }
                return option?.value || "";
              }}
              renderTags={renderTags}
              isOptionEqualToValue={(option, value) => {
                return (value?.userId && option?.userId) && value.userId === option.userId
              }
              }
              getOptionDisabled={(option) =>
                !isPredOrGredUser && option?.userId === currentUserId && isBookingForSelf
              }
              selectOnFocus
              clearOnBlur
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={isInformationMode ? WHOM_TAB_LABEL.INFORMATION_FROM : WHOM_TAB_LABEL.BOOKING_FOR}
                  variant="filled"
                  required={!isInformationMode}
                  error={
                    formik.touched["bookingFor"] === true &&
                      formik.values["bookingFor"].length === 0
                      ? true
                      : false
                  }
                  helpertext={
                    formik.touched["bookingFor"] === true &&
                      formik.values["bookingFor"].length === 0 &&
                      formik.errors["bookingFor"]
                      ? formik.errors["bookingFor"]
                      : ""
                  }
                />
              )}
              sx={GridAutoCompleteStyles?.gridAutoCompleteLableShrink}
            />
            {creatorAccessError && (
              <OwcTypography
                color="error"
                data-testid="whom-tab-creator-access-error"
              >
                You do not have access to all selected booking-controlled equipment and cannot book for others.
              </OwcTypography>
            )}

            <div className={styles.projectContainer} >
              {isPredUser && (
                <div style={{ display: "flex", alignItems: "center", marginBottom: "8px", marginTop: "4px", minHeight: "40px", "margin": "0 10px" }}>
                  <OwcCheckbox
                    checked={manualProjectInput}
                    style={{ margin: 0, height: "100%", display: "flex", alignItems: "center" }}
                    onInputChange={() => {
                      const next = !manualProjectInput;
                      setManualProjectInput(next);

                      if (next) {
                        formik.setFieldValue("redPandaProjects", []);
                      } else {
                        formik.setFieldValue("project", "");
                      }
                    }}
                  />
                  <OwcTypography style={{ fontFamily: "inherit", fontSize: "16px",  whiteSpace: "nowrap" }}>
                    Enter project manually
                  </OwcTypography>
                </div>
              )}
              {!manualProjectInput ? (
                <Autocomplete
                  multiple
                  disableCloseOnSelect
                  style={{ flex: 1 }}
                  popupIcon={null}
                  forcePopupIcon={false}
                  data-testid="whom-tab-field-project"
                  sx={{
                    ...GridAutoCompleteStyles?.gridAutoCompleteLableShrink,
                    "& .MuiFilledInput-root": {
                      paddingTop: "26px",
                      paddingBottom: "6px",
                    },
                    "& .MuiAutocomplete-tag": {
                      marginTop: "4px",
                    },
                  }}
                  options={projectOptions}
                  required={isPredUser && !isInformationMode}
                  value={projectOptions.filter((opt) =>
                    (formik.values.redPandaProjects || []).some(
                      (p) =>
                        (p.projectId && opt.value.projectId && p.projectId === opt.value.projectId)
                    )
                  )}
                  getOptionLabel={(option) => option.name || option.value?.themeNumber || "-"}
                  isOptionEqualToValue={(opt, val) =>
                    (opt.value?.projectId && val.value?.projectId && opt.value.projectId === val.value.projectId)
                  }
                  onChange={(_event, newValue) => {
                    const limited =
                      newValue.length > 10 ? newValue.slice(0, 10) : newValue;

                    const sortedSelected = [...limited].sort((a, b) =>
                      compareProjects(a?.value, b?.value)
                    );

                    if (newValue.length > 10) {
                      snackbarService.show({
                        message: "You can select up to 10 projects.",
                        type: "warning",
                        duration: DURATION,
                      });
                    }

                    formik.setFieldValue(
                      "redPandaProjects",
                      sortedSelected.map((p) => p.value)
                    );
                  }}
                  renderInput={(params) => {
                    const hasSelectedProjects = (formik.values.redPandaProjects || []).length > 0;
                    const hideRefresh = hasSelectedProjects && projectOptions.length;

                    return (
                      <div style={{ position: "relative", width: "100%" }}>
                        <TextField
                          {...params}
                          label={WHOM_TAB_LABEL.PROJECTS}
                          variant="filled"
                          required={isPredUser && !isInformationMode}
                          error={
                            formik.touched["redPandaProjects"] &&
                            Boolean(formik.errors["redPandaProjects"])
                          }
                          helperText={
                            formik.touched["redPandaProjects"] && formik.errors["redPandaProjects"]
                              ? formik.errors["redPandaProjects"]
                              : ""
                          }
                          InputProps={{
                            ...params.InputProps,
                            style: {
                              ...(params.InputProps?.style || {}),
                              paddingRight: "44px",
                            },
                          }}
                        />

                        {(isPredUser && !!localStorage.getItem("secondaryAuthAccessToken") && !hideRefresh) && (
                          <OwcIconButton
                            type="filled"
                            icon="refresh"
                            flat
                            disabled={isRefreshingProjects}
                            className={isRefreshingProjects ? styles.spin : undefined}
                            onClick={(ev) => {
                              ev.preventDefault();
                              ev.stopPropagation();
                              refreshRedPandaProjects();
                            }}
                            style={{
                              position: "absolute",
                              right: "6px",
                              top: "50%",
                              transform: "translateY(-50%)",
                              zIndex: 2,
                            }}
                            aria-label="Refresh RedPanda projects"
                          />
                        )}
                      </div>
                    )
                  }}
                />
              ) : (
                <OwcInput
                  data-testid="whom-tab-field-project"
                  label={WHOM_TAB_LABEL.PROJECTS + ((isPredUser && !isInformationMode) ? ' *' : '')}
                  style={{ width: "100%" }}
                  name="project"
                  value={formik.values.project}
                  onBlur={formik.handleBlur}
                  maxLength={30}
                  minLength={0}
                  required={isPredUser && !isInformationMode}
                  onInputChange={(ev) => {
                    formik.setFieldValue("project", ev.detail);
                  }}
                >
                  {formik.values.project !== "" && (
                    <OwcIcon
                      slot="suffix"
                      type="filled"
                      name="circle_clear"
                      onClick={() => formik.setFieldValue("project", "", true)}
                      id="whom-tab-field-project-clear"
                    />
                  )}
                </OwcInput>
              )}
            </div>
            <div className={styles.textFilled} style={{ paddingBottom: 0 }}>
              <RichDescriptionEditor
                testId="whom-tab-field-description"
                ariaLabel={
                  WHOM_TAB_LABEL.DESCRIPTION +
                  ((!isPredUser || isInformationMode) ? ' *' : '')
                }
                label={
                  WHOM_TAB_LABEL.DESCRIPTION +
                  ((!isPredUser || isInformationMode) ? ' *' : '')
                }
                value={formik.values.richDescription || ""}
                onChange={(md) => {
                  formik.setFieldValue("richDescription", md);
                  formik.setFieldValue(
                    "description",
                    deriveShortDescription(md, INFORMATION_BOOKING_DESCRIPTION_MAX),
                    true
                  );
                }}
                onBlur={() => formik.setFieldTouched("description", true)}
                maxLength={
                  isInformationMode
                    ? INFORMATION_BOOKING_DESCRIPTION_MAX
                    : MAX_RICH_DESCRIPTION_LENGTH
                }
                errorText={formik.touched["description"] ? formik.errors["description"] : undefined}
              />
            </div>
            {bookingKind === "Run" && (
              <div className={styles.textFilled} style={{ paddingBottom: "20px", display: 'flex' }}>
                <ElnExperimentField value={formik.values.elnId} onValueChange={(elnId) => formik.setFieldValue('elnId', elnId)} client={client} />
              </div>
            )}

            <div className={styles.textFilled}>
              {/* Lab Safety Declaration Section - Only show if equipment requires hazardous approval */}
              {bookingKind === "Run" && showHazardousDeclaration && (
                <HazardousDeclaration />
              )}

              {bookingKind === "Run" && showAssistedDecleration && (
                <AssistenceDeclaration>
                  {formik.values.withAssistance && isBookingAdmin && canAssignAssistantAtCreation && (
                    <div style={{ display: "flex", gap: "1rem", alignItems: "flex-start", paddingBottom: "20px" }}>
                      <AssistantSelect
                        width="100%"
                        selectedAssistantId={formik.values.assistant || ""}
                        users={usersList || []}
                        style={{ flex: 1 }}
                        onAssistantChange={(userId) => {
                          formik.setFieldValue("assistant", userId || "");
                          if (!userId) {
                            formik.setFieldValue("assistentTime", null);
                          }
                        }}
                        required={false}
                      />
                      <OwcInput
                        type="number"
                        label="Duration"
                        value={formik.values.assistentTime ?? ""}
                        min="0.5"
                        step="0.5"
                        placeholder="Leave empty for auto"
                        onInputChange={(event) => {
                          const raw = event.target?.value;
                          formik.setFieldValue("assistentTime", raw ? parseFloat(raw) : null);
                        }}
                        style={{ flex: "0 0 160px" }}
                      >
                        <div slot="suffix">h</div>
                      </OwcInput>
                    </div>
                  )}
                </AssistenceDeclaration>
              )}
            </div>
          </div>
        </div>
      </div>
      <PopOverModal
        title={popoverObject?.equipmentData?.shortIdentifier}
        data={popoverObject}
        closeTitle="Close"
        onDialogCloseCallBack={() =>
          setpopoverObject({
            equipmentData: {},
            eventData: {},
          })
        }
        {...dialogProps}
      >
        <PopoverAttributesDetails
          title={ATTRIBUTES_HEADING}
          metaData={
            popoverObject?.equipmentData?.entryType === entryType.cluster
              ? CLUSTER_DETAILS_ATTRIBUTE_META
              : STANDALONE_ATTRIBUTE_META
          }
          data={equipmentData}
        />
        {popoverObject?.equipmentData?.entryType === entryType.cluster && (
          <PopOverClusterAgGridTable data={[popoverObject?.equipmentData]} />
        )}
      </PopOverModal>
    </>
  );
};

export default compose(
  connect(null, {
    loadBookingInstruments: loadBookingInstrumentsAction,
    loadPendingReservations: loadPendingReservationsAction,
    loadUserProjects: loadUserProjectsAction
  }),
  withApollo,
)(ForWhomMainPage);
